import { Injectable } from "@angular/core";

const STORAGE_KEY = "treeslo_view_state_cache";

type ViewStateCacheStore = Record<string, unknown>;

@Injectable({
  providedIn: "root",
})
export class ViewStateCacheService {
  /** Returns persisted state for a view key, or undefined when missing/invalid. */
  getState<T>(viewKey: string): T | undefined {
    const normalizedKey = this.normalizeViewKey(viewKey);
    const store = this.readStore();
    const value = store[normalizedKey];

    if (value === undefined || value === null) {
      return undefined;
    }

    return value as T;
  }

  /** Replaces the full persisted state for a view key. */
  setState<T>(viewKey: string, state: T): void {
    const normalizedKey = this.normalizeViewKey(viewKey);
    const store = this.readStore();
    store[normalizedKey] = state;
    this.writeStore(store);
  }

  /** Shallow-merges partial state into an existing entry (creates one if missing). */
  patchState<T extends object>(viewKey: string, partial: Partial<T>): void {
    const normalizedKey = this.normalizeViewKey(viewKey);
    const store = this.readStore();
    const current = (store[normalizedKey] ?? {}) as T;

    store[normalizedKey] = {
      ...current,
      ...partial,
    };
    this.writeStore(store);
  }

  removeState(viewKey: string): void {
    const normalizedKey = this.normalizeViewKey(viewKey);
    const store = this.readStore();

    if (!(normalizedKey in store)) {
      return;
    }

    delete store[normalizedKey];
    this.writeStore(store);
  }

  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  /** Normalizes route paths and URLs into stable view keys (no leading slash, no query). */
  normalizeViewKey(viewKey: string): string {
    const withoutQuery = viewKey.split("?")[0];
    return withoutQuery.startsWith("/")
      ? withoutQuery.slice(1)
      : withoutQuery;
  }

  private readStore(): ViewStateCacheStore {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw) as ViewStateCacheStore;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeStore(store: ViewStateCacheStore): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
      console.warn("ViewStateCacheService: unable to persist view state", error);
    }
  }
}
